import { Country } from 'react-phone-number-input'

import { CustomAddressConfiguration } from 'store/types/settings'
import { BaseAddress } from 'types/global'
import { formatAddress } from 'utils/address'

import { DropdownItem } from 'components/Dropdown/types'
import { PlaceDetailsResponse, Prediction } from 'services/place-search'
import { ThemeColor } from 'utils/colors'

const ADDRESS_LINE_1_PLACE_FALLBACK_TYPES = [
  ['plus_code', 'street_address'],
  [
    'room',
    'floor',
    'subpremise',
    'premise',
    'establishment',
    'street_number',
    'route',
    'sublocality_level_2',
    'sublocality_level_1',
    'administrative_area_level_3',
  ],
]

export const createAddress = (addressLine1: string, country: Country): BaseAddress => ({
  addressLine1,
  country,
  formatted: formatAddress({ addressLine1, country }),
})

export const getAddress = ({
  placeData,
  details,
}: {
  placeData: google.maps.places.AutocompletePrediction
  details: string | google.maps.places.PlaceResult
}): BaseAddress => {
  if (typeof details === 'string') {
    return {
      addressLine1: details || placeData?.description || '',
      country: '',
      formatted: details || placeData?.description || null,
    }
  }

  const address: BaseAddress = {
    addressLine1:
      getAddressLine1FromPlace(details) ??
      details.address_components?.[0]?.long_name ??
      placeData.structured_formatting?.main_text ??
      '',
    addressLine2: null,
    country: '',
    ward: null,
    formatted: placeData?.description || details?.formatted_address || null,
    postalCode: null,
  }

  // Parsing is based on https://developers.google.com/maps/documentation/geocoding/requests-geocoding#Types
  details.address_components?.forEach((i) => {
    i.types.forEach((type) => {
      switch (type) {
        case 'administrative_area_level_1':
          address.province = i.long_name
          break
        case 'administrative_area_level_2':
          address.city = i.long_name
          break
        case 'locality':
          if (address.city) {
            address.addressLine2 = address.addressLine2 ? address.addressLine2 + `, ${i.long_name}` : i.long_name
          } else {
            address.city = i.long_name
          }
          break
        case 'neighborhood':
          address.addressLine2 = address.addressLine2 ? address.addressLine2 + `, ${i.long_name}` : i.long_name
          break
        case 'country':
          address.country = i.long_name
          break
        case 'postal_code':
          address.postalCode = i.long_name
          break
        default:
      }
    })
  })

  return address
}

const getAddressLine1FromPlace = (place: google.maps.places.PlaceResult): string | null => {
  const result: string[] = []

  ADDRESS_LINE_1_PLACE_FALLBACK_TYPES.forEach((level) => {
    // Skip next level iteration when value is already resolved
    if (result.length > 0) return

    level.forEach((i) => {
      place.address_components?.forEach((o) => {
        if (o.types.includes(i)) {
          result.push(o.long_name)
        }
      })
    })
  })

  if (result.length > 0) {
    return result.join(' ')
  }

  return null
}

const getCRMPlaceMapByGeoType = (
  address_components: PlaceDetailsResponse['address_components'],
  googleGeocodeMap: string | string[],
): string | null => {
  if (Array.isArray(address_components)) {
    if (typeof googleGeocodeMap === 'string') {
      return address_components?.find((i) => i.types.includes(googleGeocodeMap))?.long_name ?? null
    }

    const result: string[] = []
    googleGeocodeMap.forEach((i) => {
      if (Array.isArray(address_components)) {
        address_components?.forEach((o) => {
          if (o.types.includes(i)) {
            result.push(o.long_name)
          }
        })
      }
    })

    return result?.join(' ') ?? null
  }

  return null
}
// not using can be removed once central place search api feat is finished
export const mapCRMPlaceResultToAddressForm = (
  placeResult: string | PlaceDetailsResponse['address_components'],
  addressConfigs: CustomAddressConfiguration[],
): BaseAddress => {
  let address: BaseAddress = {
    addressLine1: '',
    addressLine2: null,
    ward: null,
    city: null,
    country: '',
    formatted: null,
    postalCode: null,
    province: null,
    suburb: null,
    lat: null,
    lng: null,
  }

  if (typeof placeResult === 'string') {
    return {
      ...address,
      addressLine1: placeResult,
      formatted: placeResult,
    }
  }

  addressConfigs.forEach((config) => {
    if (!config.isEnabled || !config.googleGeocodeMap || !Object.keys(address).includes(config.accessor)) {
      return
    }

    address = {
      ...address,
      [config.accessor]: getCRMPlaceMapByGeoType(placeResult, config.googleGeocodeMap),
    }
  })

  return address
}

const getPlaceMapByGeoType = (
  place: google.maps.places.PlaceResult,
  googleGeocodeMap: string | string[],
): string | null => {
  if (typeof googleGeocodeMap === 'string') {
    return place.address_components?.find((i) => i.types.includes(googleGeocodeMap))?.long_name ?? null
  }

  const result: string[] = []
  googleGeocodeMap.forEach((i) => {
    place.address_components?.forEach((o) => {
      if (o.types.includes(i)) {
        result.push(o.long_name)
      }
    })
  })

  return result?.join(' ') ?? null
}

export const mapGoogleResultToAddressForm = (
  placeResult: string | google.maps.places.PlaceResult,
  addressConfigs: CustomAddressConfiguration[],
): BaseAddress => {
  let address: BaseAddress = {
    addressLine1: '',
    addressLine2: null,
    ward: null,
    city: null,
    country: '',
    formatted: null,
    postalCode: null,
    province: null,
    suburb: null,
    lat: null,
    lng: null,
  }

  if (typeof placeResult === 'string') {
    return {
      ...address,
      addressLine1: placeResult,
      formatted: placeResult,
    }
  }

  addressConfigs.forEach((config) => {
    if (!config.isEnabled || !config.googleGeocodeMap || !Object.keys(address).includes(config.accessor)) {
      return
    }

    address = {
      ...address,
      [config.accessor]: getPlaceMapByGeoType(placeResult, config.googleGeocodeMap),
    }
  })

  return address
}

export const processPredictions = (predictions: Prediction[]): DropdownItem<string>[] =>
  predictions.map((i) => ({
    data: i,
    icon: ['far', 'location-dot'],
    iconColor: 'gray5' as ThemeColor,
    label: i.formatted_address,
    value: i.place_id,
  }))

const stripDiacritics = (str: string) => str.normalize('NFD').replace(/[\u0300-\u036f]/g, '')

export const filteredOptions = (value: string, defaultOptions?: DropdownItem<string>[]) =>
  defaultOptions?.filter((i) => {
    const input = stripDiacritics(String(value)).toLowerCase()
    const label = stripDiacritics(String(i.label)).toLowerCase()
    const val = stripDiacritics(String(i.value)).toLowerCase()

    return label.includes(input) || val.includes(input)
  })
